import React from 'react';
import { i18n } from '@international/mastodon-i18n';

import LoginForm from 'components/Reports/LoginForm';
import MainContainer from 'components/MainContainer';
import Loading from 'components/Loading';
import { fetchCandidateReports, clearLoggedOut } from 'actions';
import ToastNotification from 'components/ToastNotification';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useSearchParams, Navigate } from 'react-router-dom';
import useAuthGateState from 'hooks/useAuthGateState';
import useAuthReady from 'utils/useAuthReady';
import useUnifiedLoginKickoff from 'utils/useUnifiedLoginKickoff';
import { safeReturnPath } from 'utils/returnPath';
import { CHECKR_REGION, PATHS } from '../../constants';

const Login: React.FC = () => {
  const { processing, error } = useSelector((state: any) => state.portal);
  const {
    portalAuth: { loggedOut },
  } = useAuthGateState();
  const { loading: authLoading, isLoggedIn, token } = useAuthReady();
  const { holdsLogin: unifiedKickoffHoldsLogin } = useUnifiedLoginKickoff();
  const dispatch = useDispatch();
  const { state, hash, search } = useLocation();
  const [searchParams] = useSearchParams();

  const handleSubmit = React.useCallback(
    (values: any) => {
      dispatch(fetchCandidateReports(values));
    },
    [dispatch],
  );
  if (isLoggedIn || token) {
    const target =
      safeReturnPath(state?.prevLocation) || PATHS.REPORTS_OVERVIEW;
    return <Navigate to={target} />;
  }
  if (authLoading) {
    return <Loading />;
  }
  if (unifiedKickoffHoldsLogin) {
    return <Loading />;
  }
  const forceSsn = searchParams.get('force') === 'ssn';
  if (!forceSsn && CHECKR_REGION !== 'EU') {
    return <Navigate to={PATHS.WORKOS_LOGIN} replace state={state} />;
  }
  if (error && hash.indexOf('login_error') < 0) {
    return <Navigate to={`${search}#login_error`} state={state} />;
  }
  return (
    <MainContainer>
      {loggedOut && (
        <ToastNotification
          title={i18n.getStr('components.successFullyLoggedOut')}
          kind='success'
          onClose={() => {
            dispatch(clearLoggedOut());
          }}
        />
      )}
      <LoginForm
        onSubmit={handleSubmit}
        processing={processing}
        fetchError={error}
        loginSource='LoginForm'
      />
    </MainContainer>
  );
};

export default Login;
